import { useState, useMemo } from 'react';
import { Calculator, TrendingUp, DollarSign, ArrowDown, Sparkles } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import type { Brokerage } from '@/contracts/brokerage';
import { calculateCommission, formatCurrency } from '@/contracts/brokerage';

interface CommissionCalculatorProps {
  brokerage: Brokerage;
  className?: string;
}

export function CommissionCalculator({ brokerage, className }: CommissionCalculatorProps) {
  const [annualSalesVolume, setAnnualSalesVolume] = useState<number>(2000000);
  const [numberOfTransactions, setNumberOfTransactions] = useState<number>(10);
  const [avgCommissionPercent, setAvgCommissionPercent] = useState<number>(2.5);

  const result = useMemo(() => {
    return calculateCommission(brokerage, {
      annualSalesVolume,
      numberOfTransactions,
      avgCommissionPercent,
    });
  }, [brokerage, annualSalesVolume, numberOfTransactions, avgCommissionPercent]);

  const formatInput = (value: number) => {
    return new Intl.NumberFormat('en-US').format(value);
  };

  const parseInput = (value: string) => {
    return parseFloat(value.replace(/,/g, '')) || 0;
  };

  return (
    <Card className={className}>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-lg">
          <Calculator className="h-5 w-5 text-primary" />
          Commission Calculator
        </CardTitle>
        <p className="text-sm text-muted-foreground">
          See how much you'd make with {brokerage.name}
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Inputs */}
        <div className="grid gap-4">
          <div>
            <Label htmlFor="salesVolume" className="text-sm">Annual Sales Volume</Label>
            <div className="relative mt-1.5">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
              <Input
                id="salesVolume"
                type="text"
                value={formatInput(annualSalesVolume)}
                onChange={(e) => setAnnualSalesVolume(parseInput(e.target.value))}
                className="pl-7"
              />
            </div>
          </div>
          
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="transactions" className="text-sm"># of Transactions</Label>
              <Input
                id="transactions"
                type="number"
                value={numberOfTransactions}
                onChange={(e) => setNumberOfTransactions(parseInt(e.target.value) || 0)}
                className="mt-1.5"
              />
            </div>
            <div>
              <Label htmlFor="commissionPercent" className="text-sm">Avg Commission %</Label>
              <div className="relative mt-1.5">
                <Input
                  id="commissionPercent"
                  type="number"
                  step="0.1"
                  value={avgCommissionPercent}
                  onChange={(e) => setAvgCommissionPercent(parseFloat(e.target.value) || 0)}
                  className="pr-7"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground">%</span>
              </div>
            </div>
          </div>
        </div>

        <Separator />

        {/* Results Breakdown */}
        <div className="space-y-2 text-sm">
          <div className="flex justify-between">
            <span className="text-muted-foreground">Gross Commission Income</span>
            <span className="font-medium">{formatCurrency(result.grossCommission)}</span>
          </div>
          
          <div className="flex justify-between text-destructive/80">
            <span className="flex items-center gap-1">
              <ArrowDown className="h-3 w-3" />
              Brokerage Split ({100 - (brokerage.split_starting_percent || 70)}%)
              {result.capApplied && (
                <Badge variant="outline" className="ml-1 text-xs py-0 h-5 bg-emerald-50 text-emerald-700 border-emerald-200">
                  <Sparkles className="h-3 w-3 mr-1" />
                  Cap Hit
                </Badge>
              )}
            </span>
            <span>-{formatCurrency(result.brokerSplit)}</span>
          </div>
          
          {result.monthlyFees > 0 && (
            <div className="flex justify-between text-destructive/80">
              <span className="flex items-center gap-1">
                <ArrowDown className="h-3 w-3" />
                Monthly Fees (12 months)
              </span>
              <span>-{formatCurrency(result.monthlyFees)}</span>
            </div>
          )}
          
          {result.transactionFees > 0 && (
            <div className="flex justify-between text-destructive/80">
              <span className="flex items-center gap-1">
                <ArrowDown className="h-3 w-3" />
                Transaction Fees ({numberOfTransactions} deals)
              </span>
              <span>-{formatCurrency(result.transactionFees)}</span>
            </div>
          )}
          
          {result.otherFees > 0 && (
            <div className="flex justify-between text-destructive/80">
              <span className="flex items-center gap-1">
                <ArrowDown className="h-3 w-3" />
                Royalty/Franchise Fees
              </span>
              <span>-{formatCurrency(result.otherFees)}</span>
            </div>
          )}
        </div>

        <Separator />

        {/* Net Income */}
        <div className="bg-primary/5 rounded-lg p-4 -mx-2">
          <div className="flex justify-between items-center">
            <div>
              <p className="text-sm font-medium text-muted-foreground">Your Net Income</p>
              <p className="text-2xl font-bold text-primary">{formatCurrency(result.netIncome)}</p>
            </div>
            <div className="text-right">
              <p className="text-sm text-muted-foreground">Effective Split</p>
              <p className="text-lg font-semibold">
                {result.grossCommission > 0 
                  ? Math.round((result.netIncome / result.grossCommission) * 100) 
                  : 0}%
              </p>
            </div>
          </div>
          
          {result.capApplied && result.capSavings > 0 && (
            <div className="mt-3 pt-3 border-t border-primary/10">
              <div className="flex items-center gap-2 text-emerald-700">
                <TrendingUp className="h-4 w-4" />
                <span className="text-sm font-medium">
                  You saved {formatCurrency(result.capSavings)} by hitting the cap!
                </span>
              </div>
            </div>
          )}
        </div>

        {/* Revenue Share / Stock Note */}
        {(brokerage.has_revenue_share || brokerage.has_stock_awards) && (
          <div className="text-xs text-muted-foreground bg-muted/50 rounded-lg p-3 space-y-1">
            {brokerage.has_revenue_share && (
              <p>
                <DollarSign className="h-3 w-3 inline mr-1" />
                <strong>Revenue Share:</strong> {brokerage.revenue_share_description || 'Additional income from agent referrals not included above.'}
              </p>
            )}
            {brokerage.has_stock_awards && (
              <p>
                <TrendingUp className="h-3 w-3 inline mr-1" />
                <strong>Stock Awards:</strong> {brokerage.stock_awards_description || 'Equity awards not included above.'}
              </p>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
